import { Link } from "@tanstack/react-router"
import { Brain, Cpu, Eye, FileText, IdCard, Plus, SlidersHorizontal, Wrench } from "lucide-react"
import { useId } from "react"
import { useController, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { AgentDetail } from "@/api/generated/AgentDetail"
import type { ModelView } from "@/api/generated/ModelView"
import { REASONING_EFFORTS, useAgentTags } from "@/api/queries/agents"
import { useModels } from "@/api/queries/providers"
import {
  FieldShell,
  NumberField,
  SelectField,
  SwitchField,
  TextareaField,
  TextField,
} from "@/components/app/fields"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { formatCost, formatTokens } from "@/features/runs/format"
import { PromptEditor } from "@/features/studio/agent-editor/prompt-editor"
import { EditorSection } from "@/features/studio/agent-editor/section"
import type { AgentFormValues } from "@/features/studio/agent-form"
import { TagBadge, TagPicker } from "@/features/studio/tags"
import { cn } from "@/lib/utils"

function TemperatureField() {
  const { t } = useTranslation()
  const id = useId()
  const { control } = useFormContext<AgentFormValues>()
  const { field, fieldState } = useController({ control, name: "temperature" })
  const value = Number.isFinite(field.value) ? field.value : 0.7
  const hint =
    value <= 0.3
      ? t("studio.agents.fields.temperatureFocused")
      : value >= 1.2
        ? t("studio.agents.fields.temperatureWild")
        : value >= 0.8
          ? t("studio.agents.fields.temperatureCreative")
          : t("studio.agents.fields.temperatureBalanced")
  return (
    <FieldShell
      id={id}
      label={t("studio.agents.fields.temperature")}
      description={t("studio.agents.fields.temperatureHint", { hint })}
      error={fieldState.error}
    >
      <div className="flex items-center gap-3">
        <Slider
          value={[value]}
          min={0}
          max={2}
          step={0.05}
          onValueChange={(v) => field.onChange(Math.round((Array.isArray(v) ? v[0]! : v) * 100) / 100)}
          aria-label={t("studio.agents.fields.temperature")}
          className="flex-1"
        />
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          max={2}
          step={0.05}
          value={value}
          onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          onBlur={field.onBlur}
          className="h-7 w-18 text-right tabular-nums"
        />
      </div>
    </FieldShell>
  )
}

function TagsField() {
  const { t } = useTranslation()
  const id = useId()
  const tags = useAgentTags()
  const { control } = useFormContext<AgentFormValues>()
  const { field } = useController({ control, name: "tag_ids" })
  const selected = field.value.map((tid) => tags.data?.find((x) => x.id === tid)).filter((x) => !!x)
  return (
    <FieldShell id={id} label={t("studio.agents.fields.tags")}>
      <div className="flex min-h-8 flex-wrap items-center gap-1.5">
        {selected.map((tag) => (
          <TagBadge
            key={tag.id}
            tag={tag}
            onRemove={() => field.onChange(field.value.filter((x) => x !== tag.id))}
          />
        ))}
        <TagPicker value={field.value} onChange={field.onChange}>
          <Button type="button" variant="outline" size="xs">
            <Plus />
            {t("studio.agents.fields.addTag")}
          </Button>
        </TagPicker>
      </div>
    </FieldShell>
  )
}

function ModelSummary({ model }: { model: ModelView | undefined }) {
  const { t } = useTranslation()
  if (!model) return null
  const parts = [
    `${model.provider_name} · ${model.model}`,
    model.context_length ? t("studio.agents.model.context", { n: formatTokens(model.context_length) }) : null,
    model.input_per_1m != null && model.output_per_1m != null
      ? t("studio.agents.model.price", {
          input: formatCost(model.input_per_1m, model.currency ?? "USD"),
          output: formatCost(model.output_per_1m, model.currency ?? "USD"),
        })
      : null,
  ].filter(Boolean)
  return (
    <div className="text-muted-foreground -mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span>{parts.join(" · ")}</span>
      {model.supports_vision && (
        <span className="inline-flex items-center gap-1">
          <Eye className="size-3" />
          {t("studio.models.vision")}
        </span>
      )}
      {model.supports_tools && (
        <span className="inline-flex items-center gap-1">
          <Wrench className="size-3" />
          {t("studio.models.tools")}
        </span>
      )}
      {model.fallback_model_name && (
        <span>{t("studio.agents.model.fallback", { name: model.fallback_model_name })}</span>
      )}
    </div>
  )
}

function EffortField({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation()
  const id = useId()
  const { control } = useFormContext<AgentFormValues>()
  const { field } = useController({ control, name: "reasoning_effort" })
  return (
    <FieldShell
      id={id}
      label={t("studio.agents.fields.effort")}
      description={disabled ? t("studio.agents.fields.effortDisabled") : t("studio.agents.fields.effortHint")}
    >
      <ToggleGroup
        id={id}
        variant="outline"
        size="sm"
        spacing={0}
        value={[field.value]}
        onValueChange={(v) => {
          const next = (v as string[])[0]
          if (next) field.onChange(next)
        }}
        disabled={disabled}
        className="flex-wrap"
      >
        {REASONING_EFFORTS.map((e) => (
          <ToggleGroupItem key={e} value={e} className="px-3" aria-label={t(`studio.agents.effort.${e}`)}>
            {t(`studio.agents.effort.${e}`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </FieldShell>
  )
}

export function ConfigureTab({ agent: _agent }: { agent: AgentDetail }) {
  const { t } = useTranslation()
  const { control } = useFormContext<AgentFormValues>()
  const models = useModels()
  const modelId = useWatch({ control, name: "model_id" })
  const thinking = useWatch({ control, name: "thinking_enabled" })
  const model = models.data?.find((m) => m.id === modelId)
  const kind = model?.provider_kind
  const cacheOptions = [
    { value: "5m", label: t("studio.agents.cache.5m") },
    { value: "1h", label: t("studio.agents.cache.1h") },
  ]

  const sections = [
    { id: "agent-identity", label: t("studio.agents.sections.identity.title") },
    { id: "agent-instructions", label: t("studio.agents.sections.instructions.title") },
    { id: "agent-model", label: t("studio.agents.sections.model.title") },
    { id: "agent-reasoning", label: t("studio.agents.sections.reasoning.title") },
    { id: "agent-behaviour", label: t("studio.agents.sections.behaviour.title") },
  ]

  return (
    <div className="grid items-start gap-8 @5xl:grid-cols-[minmax(0,56rem)_11rem]">
      <div className="grid min-w-0 gap-5">
        <EditorSection
          id="agent-identity"
          icon={IdCard}
          title={t("studio.agents.sections.identity.title")}
          description={t("studio.agents.sections.identity.description")}
        >
          <div className="grid gap-5 @lg:grid-cols-2">
            <TextField
              control={control}
              name="name"
              label={t("studio.agents.fields.name")}
              required
              maxLength={255}
            />
            <TagsField />
          </div>
          <TextareaField
            control={control}
            name="description"
            label={t("studio.agents.fields.description")}
            description={t("studio.agents.fields.descriptionHint")}
            placeholder={t("studio.agents.fields.descriptionPlaceholder")}
            rows={2}
            maxLength={2000}
          />
        </EditorSection>

        <EditorSection
          id="agent-instructions"
          icon={FileText}
          title={t("studio.agents.sections.instructions.title")}
          description={t("studio.agents.sections.instructions.description")}
        >
          <PromptEditor control={control} />
        </EditorSection>

        <EditorSection
          id="agent-model"
          icon={Cpu}
          title={t("studio.agents.sections.model.title")}
          description={t("studio.agents.sections.model.description")}
          actions={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              nativeButton={false}
              render={<Link to="/studio/models" />}
            >
              {t("studio.agents.model.manage")}
            </Button>
          }
        >
          <SelectField
            control={control}
            name="model_id"
            label={t("studio.agents.fields.model")}
            required
            options={(models.data ?? []).map((m) => ({
              value: m.id,
              label: m.name,
              description: `${m.provider_name} · ${m.model}`,
            }))}
          />
          <ModelSummary model={model} />
          <TemperatureField />
          <div className="grid gap-5 @lg:grid-cols-3">
            <NumberField
              control={control}
              name="top_p"
              label={t("studio.agents.fields.topP")}
              description={t("studio.agents.fields.topPHint")}
              min={0}
              max={1}
              step={0.01}
              nullable
              placeholder={t("studio.agents.fields.default")}
            />
            <NumberField
              control={control}
              name="top_k"
              label={t("studio.agents.fields.topK")}
              description={
                kind === "openai"
                  ? t("studio.agents.fields.topKUnsupported")
                  : t("studio.agents.fields.topKHint")
              }
              min={0}
              step={1}
              nullable
              placeholder={t("studio.agents.fields.default")}
            />
            <NumberField
              control={control}
              name="max_tokens"
              label={t("studio.agents.fields.maxTokens")}
              description={t("studio.agents.fields.maxTokensHint")}
              min={1}
              step={1}
              nullable
              unit={t("studio.agents.fields.tokensUnit")}
              placeholder={
                model?.max_output_tokens ? String(model.max_output_tokens) : t("studio.agents.fields.default")
              }
            />
          </div>
        </EditorSection>

        <EditorSection
          id="agent-reasoning"
          icon={Brain}
          title={t("studio.agents.sections.reasoning.title")}
          description={t("studio.agents.sections.reasoning.description")}
        >
          <SwitchField
            control={control}
            name="thinking_enabled"
            variant="card"
            label={t("studio.agents.fields.thinking")}
            description={t("studio.agents.fields.thinkingHint")}
          />
          <EffortField disabled={!thinking} />
        </EditorSection>

        <EditorSection
          id="agent-behaviour"
          icon={SlidersHorizontal}
          title={t("studio.agents.sections.behaviour.title")}
          description={t("studio.agents.sections.behaviour.description")}
        >
          <SwitchField
            control={control}
            name="allow_as_chat_agent"
            variant="card"
            label={t("studio.agents.fields.chatAgent")}
            description={t("studio.agents.fields.chatAgentHint")}
          />
          <div className="grid gap-5 @lg:grid-cols-3">
            <NumberField
              control={control}
              name="max_steps"
              label={t("studio.agents.fields.maxSteps")}
              description={t("studio.agents.fields.maxStepsHint")}
              min={1}
              max={500}
              step={1}
              unit={t("studio.agents.fields.stepsUnit")}
            />
            <SelectField
              control={control}
              name="prompt_cache_static"
              label={t("studio.agents.fields.cacheStatic")}
              description={t("studio.agents.fields.cacheStaticHint")}
              options={cacheOptions}
            />
            <SelectField
              control={control}
              name="prompt_cache_messages"
              label={t("studio.agents.fields.cacheMessages")}
              description={t("studio.agents.fields.cacheMessagesHint")}
              options={cacheOptions}
            />
          </div>
          <p className={cn("text-muted-foreground -mt-2 text-xs", kind === "anthropic" && "hidden")}>
            {t("studio.agents.fields.cacheAnthropicOnly")}
          </p>
        </EditorSection>
      </div>
      <nav
        aria-label={t("studio.agents.sections.nav")}
        className="sticky top-6 hidden space-y-1 text-sm @5xl:block"
      >
        {sections.map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() =>
              document.getElementById(x.id)?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            className="text-muted-foreground hover:text-foreground block w-full truncate border-l py-1 pl-3 text-left transition-colors"
          >
            {x.label}
          </button>
        ))}
      </nav>
    </div>
  )
}
